/**
 * Invite Accept page — handles both logged-in and logged-out flows.
 *
 * Route: /i/:code
 *
 * - Logged-out: shows invite metadata + Sign-in / Sign-up CTAs that preserve `?next=/i/:code`.
 * - Logged-in: shows accept form (with branch picker if chapter invite has suggestedBranchIds);
 *   submits POST /api/invitations/:code/accept and shows confirmation.
 * - 410 / revoked / expired: shows friendly error state.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useAuth } from '@/hooks/useAuth';
import {
  ApiClientError,
  invitationsApi,
  chaptersApi,
  type AcceptInvitationResponse,
  type BranchSummary,
  type InvitationLookup,
} from '@/services/api';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; invite: InvitationLookup; branches: BranchSummary[] }
  | { kind: 'expired' }
  | { kind: 'not_found' }
  | { kind: 'error'; message: string };

export function InviteAcceptPage() {
  const params = useParams();
  const code = String(params['code'] ?? '');
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const navigate = useNavigate();

  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [chosenBranchId, setChosenBranchId] = useState<string>('');
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [accepted, setAccepted] = useState<AcceptInvitationResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    (async () => {
      try {
        const invite = await invitationsApi.get(code);
        let branches: BranchSummary[] = [];
        if (invite.scopeType === 'chapter' && invite.suggestedBranchIds.length > 0) {
          try {
            const all = await chaptersApi.listBranches(invite.scopeId);
            const allowed = new Set(invite.suggestedBranchIds);
            branches = all.filter((b) => allowed.has(b.id));
          } catch {
            // Non-fatal — user can still accept without picking a branch.
            branches = [];
          }
        }
        if (cancelled) return;
        setState({ kind: 'ready', invite, branches });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiClientError) {
          if (err.status === 410) {
            setState({ kind: 'expired' });
            return;
          }
          if (err.status === 404) {
            setState({ kind: 'not_found' });
            return;
          }
          setState({ kind: 'error', message: err.message });
          return;
        }
        setState({ kind: 'error', message: 'Unable to load invitation' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code]);

  const nextHref = useMemo(() => `/i/${encodeURIComponent(code)}`, [code]);
  const loginHref = `/login?next=${encodeURIComponent(nextHref)}`;
  const signupHref = `/signup?next=${encodeURIComponent(nextHref)}`;

  async function handleAccept() {
    if (state.kind !== 'ready') return;
    setAccepting(true);
    setAcceptError(null);
    try {
      const res = await invitationsApi.accept(code, chosenBranchId ? { chosenBranchId } : {});
      setAccepted(res);
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.status === 410) {
          setState({ kind: 'expired' });
          return;
        }
        setAcceptError(err.message);
      } else {
        setAcceptError('Failed to accept invitation');
      }
    } finally {
      setAccepting(false);
    }
  }

  return (
    <div className="container mx-auto px-4 py-16">
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-display font-bold mb-2">
            <span className="text-gradient">Invitation</span>
          </h1>
          <p className="text-white/60">You have been invited to join</p>
        </div>

        <div className="card">
          {state.kind === 'loading' || authLoading ? (
            <p className="text-white/70 text-center py-4">Loading invitation…</p>
          ) : state.kind === 'expired' ? (
            <div className="text-center py-4">
              <h2 className="text-xl font-semibold text-white mb-2">This invitation is no longer active</h2>
              <p className="text-white/60 mb-6">It may have expired, been revoked, or hit its use limit.</p>
              <Link to="/" className="btn-secondary">Back to home</Link>
            </div>
          ) : state.kind === 'not_found' ? (
            <div className="text-center py-4">
              <h2 className="text-xl font-semibold text-white mb-2">Invitation not found</h2>
              <p className="text-white/60 mb-6">Double-check the link you were sent.</p>
              <Link to="/" className="btn-secondary">Back to home</Link>
            </div>
          ) : state.kind === 'error' ? (
            <div className="text-center py-4">
              <h2 className="text-xl font-semibold text-white mb-2">Could not load invitation</h2>
              <p className="text-white/60 mb-6">{state.message}</p>
              <Link to="/" className="btn-secondary">Back to home</Link>
            </div>
          ) : accepted ? (
            <AcceptedView
              accepted={accepted}
              displayName={state.invite.scope?.displayName ?? 'this group'}
              onContinue={() => navigate('/member/dashboard')}
            />
          ) : (
            <>
              <InviteSummary invite={state.invite} />

              {!isAuthenticated ? (
                <div className="space-y-4 mt-6">
                  <p className="text-white/70 text-sm text-center">
                    Sign in to accept this invitation, or create a new account.
                  </p>
                  <Link to={loginHref} className="btn-primary w-full block text-center">
                    Sign In
                  </Link>
                  <Link to={signupHref} className="btn-secondary w-full block text-center">
                    Create an Account
                  </Link>
                </div>
              ) : (
                <div className="space-y-4 mt-6">
                  {state.branches.length > 0 && (
                    <div>
                      <label htmlFor="branch" className="block text-sm font-medium text-white/80 mb-2">
                        Pick your section (optional)
                      </label>
                      <select
                        id="branch"
                        value={chosenBranchId}
                        onChange={(e) => setChosenBranchId(e.target.value)}
                        className="input"
                        disabled={accepting}
                      >
                        <option value="">— Skip (you can request later) —</option>
                        {state.branches.map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.displayName ?? b.name}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-white/40 mt-1">
                        Picking a section creates a join request for that branch's admin.
                      </p>
                    </div>
                  )}

                  {acceptError && (
                    <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                      <p className="text-red-400 text-sm">{acceptError}</p>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={handleAccept}
                    disabled={accepting}
                    className="btn-primary w-full"
                  >
                    {accepting ? 'Accepting…' : `Accept invitation`}
                  </button>
                  <p className="text-xs text-white/40 text-center">
                    Accepting as <span className="text-white/70">{user?.displayName ?? user?.email}</span>.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        <div className="text-center mt-8">
          <Link to="/" className="link text-sm">Back to home</Link>
        </div>
      </div>
    </div>
  );
}

function InviteSummary({ invite }: { invite: InvitationLookup }) {
  return (
    <div className="space-y-3">
      <div>
        <p className="text-xs uppercase tracking-wide text-white/40">Scope</p>
        <p className="text-lg font-semibold text-white">
          {invite.scope?.displayName ?? `${invite.scopeType}: ${invite.scopeId}`}
        </p>
        <p className="text-xs text-white/40 capitalize">{invite.scopeType}</p>
      </div>
      <div>
        <p className="text-xs uppercase tracking-wide text-white/40">Role on join</p>
        <p className="text-white capitalize">{invite.role.replace('_', ' ')}</p>
      </div>
      <div>
        <p className="text-xs uppercase tracking-wide text-white/40">Invited by</p>
        <p className="text-white">{invite.inviterDisplay}</p>
      </div>
      <div>
        <p className="text-xs uppercase tracking-wide text-white/40">Expires</p>
        <p className="text-white">{new Date(invite.expiresAt).toLocaleString()}</p>
      </div>
    </div>
  );
}

function AcceptedView({
  accepted,
  displayName,
  onContinue,
}: {
  accepted: AcceptInvitationResponse;
  displayName: string;
  onContinue: () => void;
}) {
  return (
    <div className="text-center py-4 space-y-4">
      <h2 className="text-2xl font-semibold text-white">
        {accepted.alreadyMember ? 'You were already a member' : `Welcome to ${displayName}!`}
      </h2>
      <p className="text-white/60">
        You are now {accepted.membership.role.replace('_', ' ')} of this {accepted.membership.scopeType}.
      </p>
      {accepted.joinRequest && (
        <p className="text-sm text-white/60">
          A request to join the selected branch has been submitted for admin approval.
        </p>
      )}
      <button type="button" onClick={onContinue} className="btn-primary">
        Continue to dashboard
      </button>
    </div>
  );
}

export default InviteAcceptPage;
